import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const defaultTestimonials = [
  {
    id: 1,
    quote:
      'Synentia helped us reduce support turnaround time by 42% while improving quality controls across every escalation lane.',
    role: 'VP of Customer Operations',
    industry: 'Healthcare',
    visual:
      'https://images.unsplash.com/photo-1573497019707-1c04de26e58c?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 2,
    quote:
      'The rollout was structured, clear, and fast. We shipped real production workflows without creating governance risk.',
    role: 'Head of Digital Strategy',
    industry: 'Financial Services',
    visual:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 3,
    quote:
      'Instead of disconnected pilots, we now have one operating model for AI delivery that teams across functions can use.',
    role: 'Director of Transformation',
    industry: 'Logistics',
    visual:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
  },
];

export function Testimonial({ items = defaultTestimonials }) {
  const testimonials = items?.length ? items : defaultTestimonials;
  const [activeIndex, setActiveIndex] = useState(0);
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.12 });

  useEffect(() => {
    if (!testimonials.length) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [testimonials.length]);

  useEffect(() => {
    if (activeIndex > testimonials.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, testimonials.length]);

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const active = testimonials[activeIndex];

  return (
    <section id="testimonials" className="relative py-20 sm:py-24 lg:py-32 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div
          className={`mb-14 max-w-4xl transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className="section-eyebrow mb-4">Testimonials</p>
          <h2 className="text-[clamp(2rem,4.1vw,3.8rem)] font-semibold text-[--text-primary] leading-[1.02]">
            Trusted by Operators and Leaders
            <br />
            <span className="accent-wash">to Ship, Not Just Advise</span>
          </h2>
          <p className="mt-6 text-lg text-[--text-secondary] max-w-3xl">
            Feedback from leaders who launched production AI with clear governance, adoption, and
            measurable business results.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.14fr_0.86fr] gap-5">
          <article
            className={`relative overflow-hidden rounded-3xl soft-card border-[--border-hover] transition-all duration-700 ease-out delay-100 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="absolute inset-0 pointer-events-none">
              <img src={active.visual} alt="" className="h-full w-full object-cover opacity-[0.24]" />
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[--bg-primary]/22 to-[--bg-primary]/92" />
            </div>

            <div className="relative p-6 sm:p-8 md:p-10 lg:p-12">
              <div className="flex items-center justify-between gap-4 mb-8">
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-mono text-[--text-muted] border border-[--border] rounded-full px-3 py-1.5">
                  <Quote className="w-3.5 h-3.5 text-[--accent]" />
                  Client Voice
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goPrev}
                    className="w-10 h-10 rounded-full border border-[--border] text-[--text-secondary] hover:text-[--text-primary] hover:border-[--border-hover] flex items-center justify-center"
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="w-10 h-10 rounded-full border border-[--border] text-[--text-secondary] hover:text-[--text-primary] hover:border-[--border-hover] flex items-center justify-center"
                    aria-label="Next testimonial"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="min-h-[176px] md:min-h-[164px]">
                <AnimatePresence mode="wait">
                  <motion.blockquote
                    key={activeIndex}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -18 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="text-[1.3rem] sm:text-[1.55rem] md:text-[2rem] lg:text-[2.35rem] leading-[1.18] font-semibold text-[--text-primary] tracking-[-0.02em]"
                  >
                    "{active.quote}"
                  </motion.blockquote>
                </AnimatePresence>
              </div>

              <div className="mt-8 pt-6 border-t border-[--border] flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`author-${activeIndex}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                    className="flex w-full items-center sm:w-auto"
                  >
                    <div>
                      <p className="text-[--text-primary] text-base font-semibold">{active.role}</p>
                      <p className="text-sm text-[--text-secondary] mt-0.5">{active.industry}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="flex items-center gap-2 self-start sm:self-auto">
                  {testimonials.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={`View testimonial ${index + 1}`}
                      onClick={() => setActiveIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === activeIndex
                          ? 'w-10 bg-[--accent]'
                          : 'w-2.5 bg-[--border-hover] hover:bg-[--text-muted]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </article>

          <aside
            className={`space-y-3 transition-all duration-700 ease-out delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {testimonials.map((item, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`w-full text-left rounded-2xl border p-4 md:p-5 transition-all ${
                    isActive
                      ? 'border-[--accent] bg-[--bg-secondary] shadow-[0_18px_46px_-32px_var(--card-shadow)]'
                      : 'border-[--border] bg-[--bg-secondary]/55 hover:border-[--border-hover]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-semibold ${
                          isActive ? 'text-[--text-primary]' : 'text-[--text-secondary]'
                        }`}
                      >
                        {item.role}
                      </p>
                      <p className="text-xs text-[--text-muted] mt-0.5">{item.industry}</p>
                    </div>
                    <span
                      className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
                        isActive ? 'bg-[--accent]' : 'bg-[--border-hover]'
                      }`}
                    />
                  </div>
                </button>
              );
            })}
          </aside>
        </div>
      </div>
    </section>
  );
}
